import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { courierApi } from '@/services/api'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { formatCurrency, formatDate } from '@/lib/utils'
import { DollarSign, TrendingUp, Clock, Truck, Loader2 } from 'lucide-react'

type Period = 'today' | 'week' | 'month'

export default function EarningsPage() {
  const [period, setPeriod] = useState<Period>('week')

  const getDateRange = (p: Period) => {
    const end = new Date()
    const start = new Date()
    if (p === 'today') start.setHours(0, 0, 0, 0)
    else if (p === 'week') start.setDate(start.getDate() - 7)
    else start.setMonth(start.getMonth() - 1)
    return { startDate: start.toISOString(), endDate: end.toISOString() }
  }

  const { data: summary, isLoading: summaryLoading } = useQuery({
    queryKey: ['earnings-summary', period],
    queryFn: () => courierApi.getEarningsSummary(getDateRange(period)),
  })

  const { data: earnings, isLoading: earningsLoading } = useQuery({
    queryKey: ['earnings', period],
    queryFn: () => courierApi.getEarnings(getDateRange(period)),
  })

  const isLoading = summaryLoading || earningsLoading

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Earnings</h1>
        <p className="text-muted-foreground">Track your delivery income</p>
      </div>

      {/* Period Filter */}
      <div className="flex gap-2">
        {(['today', 'week', 'month'] as Period[]).map((p) => (
          <Button
            key={p}
            variant={period === p ? 'default' : 'outline'}
            size="sm"
            onClick={() => setPeriod(p)}
            className="capitalize"
          >
            {p === 'today' ? 'Today' : p === 'week' ? 'This Week' : 'This Month'}
          </Button>
        ))}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 gap-4">
            <Card className="col-span-2 bg-gradient-to-br from-primary to-primary/80 text-white">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm opacity-90">Total Earnings</p>
                    <p className="text-3xl font-bold">{formatCurrency(summary?.totalEarnings ?? 0)}</p>
                  </div>
                  <DollarSign className="w-12 h-12 opacity-50" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-success/10 rounded-lg">
                    <TrendingUp className="w-5 h-5 text-success" />
                  </div>
                  <div>
                    <p className="text-xl font-bold">{formatCurrency(summary?.totalTips ?? 0)}</p>
                    <p className="text-xs text-muted-foreground">Tips</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <Truck className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-xl font-bold">{summary?.totalDeliveries ?? 0}</p>
                    <p className="text-xs text-muted-foreground">Deliveries</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-accent/10 rounded-lg">
                    <Clock className="w-5 h-5 text-accent" />
                  </div>
                  <div>
                    <p className="text-xl font-bold">{formatCurrency(summary?.pendingPayout ?? 0)}</p>
                    <p className="text-xs text-muted-foreground">Pending</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-secondary/10 rounded-lg">
                    <DollarSign className="w-5 h-5 text-secondary" />
                  </div>
                  <div>
                    <p className="text-xl font-bold">{formatCurrency(summary?.averagePerDelivery ?? 0)}</p>
                    <p className="text-xs text-muted-foreground">Avg/Delivery</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Earnings History */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Recent Earnings</CardTitle>
            </CardHeader>
            <CardContent>
              {!earnings?.length ? (
                <p className="text-center text-muted-foreground py-6">No earnings in this period</p>
              ) : (
                <div className="space-y-3">
                  {earnings.map((earning) => (
                    <div key={earning.id} className="flex items-center justify-between py-2 border-b last:border-0">
                      <div>
                        <p className="font-medium">#{earning.orderNumber}</p>
                        <p className="text-xs text-muted-foreground">{formatDate(earning.earnedAt)}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-medium">{formatCurrency(earning.amount)}</p>
                        {earning.tip > 0 && (
                          <p className="text-xs text-success">+{formatCurrency(earning.tip)} tip</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  )
}
